import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AllDayToggle from '../planning/AllDayToggle';
import useEventCategories from '../planning/useEventCategories';

export default function CreateEventPopover({
  open,
  style,
  heading,
  dayIso,
  endDayIso,
  startTime,
  endTime,
  allDay,
  title,
  category,
  timeError,
  onChange,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation('planning');
  const { t: tCommon } = useTranslation('common');
  const panelRef = useRef(null);
  const { codes, labels } = useEventCategories();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    // Defer so the click/drag that opened the modal does not immediately close it
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      ref={panelRef}
      className="weekly-view-popover"
      style={style}
      role="dialog"
      aria-labelledby="create-event-heading"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-1.5 h-5 rounded-full bg-nokia-blue shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h2 id="create-event-heading" className="text-sm font-bold text-primary leading-snug">
              {heading}
            </h2>
            <button
              type="button"
              className="p-0.5 text-text-secondary hover:text-primary rounded transition-colors shrink-0"
              aria-label={tCommon('close')}
              onClick={onClose}
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        </div>
      </div>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="wv-create-day" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('startDay')}
            </label>
            <input
              id="wv-create-day"
              type="date"
              required
              value={dayIso}
              onChange={(e) => onChange?.({ dayIso: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
            />
          </div>
          <div>
            <label htmlFor="wv-create-end-day" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('endDay')}
            </label>
            <input
              id="wv-create-end-day"
              type="date"
              required
              value={endDayIso || dayIso}
              min={dayIso}
              onChange={(e) => onChange?.({ endDayIso: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
            />
          </div>
        </div>
        <AllDayToggle
          id="wv-create-all-day"
          checked={!!allDay}
          onChange={(checked) => onChange?.({ allDay: checked })}
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="wv-create-start" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('startTime')}
            </label>
            <input
              id="wv-create-start"
              type="time"
              required
              disabled={!!allDay}
              value={allDay ? '00:00' : startTime}
              onChange={(e) => onChange?.({ startTime: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="wv-create-end" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('endTime')}
            </label>
            <input
              id="wv-create-end"
              type="time"
              required
              disabled={!!allDay}
              value={allDay ? '23:59' : endTime}
              onChange={(e) => onChange?.({ endTime: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        {timeError ? (
          <p className="text-[11px] text-red-600 font-medium">{t('endMustBeAfterStart')}</p>
        ) : null}
        <div className="space-y-3">
          <span className="block text-[11px] font-semibold text-text-secondary mb-1.5">{t('category')}</span>
          <div className="category-radio-grid" role="radiogroup" aria-label={t('eventCategory')}>
            {codes.map((cat) => (
              <label key={cat}>
                <input
                  type="radio"
                  name="wv-category"
                  value={cat}
                  checked={category === cat}
                  onChange={() => onChange?.({ category: cat })}
                />
                <span>{labels[cat] || cat}</span>
              </label>
            ))}
          </div>
          <div>
            <label htmlFor="wv-create-title" className="block text-[11px] font-semibold text-text-secondary mb-1">
              {t('eventTitleOptional')} <span className="font-normal opacity-70">{t('optionalSuffix')}</span>
            </label>
            <input
              id="wv-create-title"
              type="text"
              maxLength={80}
              placeholder={t('addTitle')}
              value={title}
              onChange={(e) => onChange?.({ title: e.target.value })}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-main hover:bg-background-light rounded-lg transition-colors"
          >
            {tCommon('cancel')}
          </button>
          <button
            type="submit"
            className="px-3 py-1.5 bg-primary hover:bg-primary-hover text-white text-xs font-medium rounded-lg active:scale-[0.98] transition-all"
          >
            {tCommon('save')}
          </button>
        </div>
      </form>
    </div>
  );
}
